import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import { 
  Code, 
  RotateCcw, 
  Send, 
  ChevronDown,
  ChevronUp,
  X,
  ArrowRight,
  Briefcase,
  GraduationCap,
  Building2,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface CodingProblem {
  id: string;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  description: string;
  starterCode: string;
  targetLevel?: string[]; // Which levels this problem is good for
}

export interface UserContext {
  experienceLevel: 'junior' | 'mid' | 'senior' | 'staff';
  targetCompany: 'faang' | 'startup' | 'hedge-fund' | 'enterprise';
  pythonBackground: 'backend' | 'data-engineering' | 'ml' | 'fullstack';
}

// Problems organized by difficulty for different levels
export const CODING_PROBLEMS: CodingProblem[] = [
  {
    id: 'two-sum',
    title: 'Two Sum',
    difficulty: 'Easy',
    description: 'Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target.',
    starterCode: `def two_sum(nums: list[int], target: int) -> list[int]:
    # Your solution here
    pass

# Test cases
print(two_sum([2, 7, 11, 15], 9))  # Expected: [0, 1]
print(two_sum([3, 2, 4], 6))       # Expected: [1, 2]
`,
    targetLevel: ['junior', 'mid']
  },
  {
    id: 'valid-parentheses',
    title: 'Valid Parentheses',
    difficulty: 'Easy',
    description: 'Given a string s containing just the characters "(", ")", "{", "}", "[" and "]", determine if the input string is valid.',
    starterCode: `def is_valid(s: str) -> bool:
    # Your solution here
    pass

# Test cases
print(is_valid("()"))      # Expected: True
print(is_valid("()[]{}"))  # Expected: True
print(is_valid("(]"))      # Expected: False
`,
    targetLevel: ['junior', 'mid']
  },
  {
    id: 'merge-intervals',
    title: 'Merge Intervals',
    difficulty: 'Medium',
    description: 'Given an array of intervals, merge all overlapping intervals and return an array of non-overlapping intervals.',
    starterCode: `def merge(intervals: list[list[int]]) -> list[list[int]]:
    # Your solution here
    pass

# Test cases
print(merge([[1,3],[2,6],[8,10],[15,18]]))  # Expected: [[1,6],[8,10],[15,18]]
print(merge([[1,4],[4,5]]))                  # Expected: [[1,5]]
`,
    targetLevel: ['mid', 'senior']
  },
  {
    id: 'lru-cache',
    title: 'LRU Cache',
    difficulty: 'Medium',
    description: 'Design a data structure that follows the constraints of a Least Recently Used (LRU) cache.',
    starterCode: `class LRUCache:
    def __init__(self, capacity: int):
        # Your implementation here
        pass
    
    def get(self, key: int) -> int:
        # Return value if key exists, else -1
        pass
    
    def put(self, key: int, value: int) -> None:
        # Insert or update key-value pair
        pass

# Test cases
cache = LRUCache(2)
cache.put(1, 1)
cache.put(2, 2)
print(cache.get(1))    # Expected: 1
cache.put(3, 3)        # Evicts key 2
print(cache.get(2))    # Expected: -1
`,
    targetLevel: ['mid', 'senior', 'staff']
  },
  {
    id: 'binary-tree-level-order',
    title: 'Binary Tree Level Order Traversal',
    difficulty: 'Medium',
    description: 'Given the root of a binary tree, return the level order traversal of its nodes\' values (from left to right, level by level).',
    starterCode: `from collections import deque
from typing import Optional, List

class TreeNode:
    def __init__(self, val=0, left=None, right=None):
        self.val = val
        self.left = left
        self.right = right

def level_order(root: Optional[TreeNode]) -> List[List[int]]:
    # Your solution here
    pass

# Test: Build tree [3,9,20,null,null,15,7]
root = TreeNode(3)
root.left = TreeNode(9)
root.right = TreeNode(20, TreeNode(15), TreeNode(7))
print(level_order(root))  # Expected: [[3], [9, 20], [15, 7]]
`,
    targetLevel: ['mid', 'senior']
  },
  {
    id: 'median-two-sorted-arrays',
    title: 'Median of Two Sorted Arrays',
    difficulty: 'Hard',
    description: 'Given two sorted arrays nums1 and nums2, return the median of the two sorted arrays. The overall run time complexity should be O(log (m+n)).',
    starterCode: `def find_median_sorted_arrays(nums1: list[int], nums2: list[int]) -> float:
    # Your solution here - aim for O(log(m+n)) complexity
    pass

# Test cases
print(find_median_sorted_arrays([1, 3], [2]))        # Expected: 2.0
print(find_median_sorted_arrays([1, 2], [3, 4]))     # Expected: 2.5
print(find_median_sorted_arrays([0, 0], [0, 0]))     # Expected: 0.0
`,
    targetLevel: ['senior', 'staff']
  }
];

interface TextCodingModeProps {
  onClose: () => void;
  onSendCode: (code: string, problem: CodingProblem, userContext?: UserContext) => void;
  className?: string;
  selectedProblem?: CodingProblem;
}

const experienceLevels = [
  { id: 'junior', label: 'Junior', description: '0-2 years', icon: GraduationCap },
  { id: 'mid', label: 'Mid-Level', description: '2-5 years', icon: Briefcase },
  { id: 'senior', label: 'Senior', description: '5-8 years', icon: Target },
  { id: 'staff', label: 'Staff/Principal', description: '8+ years', icon: Building2 },
] as const;

const companyTypes = [
  { id: 'faang', label: 'FAANG / Big Tech', description: 'Google, Meta, Amazon, etc.' },
  { id: 'startup', label: 'Top Startup', description: 'VC-backed, high growth' },
  { id: 'hedge-fund', label: 'Hedge Fund / Quant', description: 'Citadel, Two Sigma, etc.' },
  { id: 'enterprise', label: 'Enterprise', description: 'Banks, consulting, large corps' },
] as const;

const pythonBackgrounds = [
  { id: 'backend', label: 'Backend / API', description: 'FastAPI, Django, Flask' },
  { id: 'data-engineering', label: 'Data Engineering', description: 'Spark, Airflow, ETL' },
  { id: 'ml', label: 'ML Engineering', description: 'PyTorch, scikit-learn, MLOps' },
  { id: 'fullstack', label: 'Full-Stack', description: 'Python + React/Vue' },
] as const;

export const TextCodingMode: React.FC<TextCodingModeProps> = ({ 
  onClose, 
  onSendCode,
  className,
  selectedProblem: initialProblem
}) => {
  const [showIntake, setShowIntake] = useState(true);
  const [userContext, setUserContext] = useState<UserContext>({
    experienceLevel: 'mid',
    targetCompany: 'startup',
    pythonBackground: 'backend'
  });
  const [problem, setProblem] = useState<CodingProblem | null>(null);
  const [code, setCode] = useState('');
  const [isProblemExpanded, setIsProblemExpanded] = useState(true);
  const [showProblemSelector, setShowProblemSelector] = useState(false);

  // Select appropriate problem based on user context
  const selectProblemForUser = () => {
    let targetDifficulty: CodingProblem['difficulty'][] = ['Easy'];
    
    // Determine difficulty based on level and company type
    if (userContext.experienceLevel === 'junior') {
      targetDifficulty = ['Easy'];
    } else if (userContext.experienceLevel === 'mid') {
      targetDifficulty = userContext.targetCompany === 'hedge-fund' ? ['Medium', 'Hard'] : ['Easy', 'Medium'];
    } else if (userContext.experienceLevel === 'senior') {
      targetDifficulty = userContext.targetCompany === 'hedge-fund' ? ['Hard'] : ['Medium', 'Hard'];
    } else {
      targetDifficulty = ['Medium', 'Hard'];
    }

    // Filter problems by difficulty
    const suitableProblems = CODING_PROBLEMS.filter(p => targetDifficulty.includes(p.difficulty));
    const selected = initialProblem || suitableProblems[Math.floor(Math.random() * suitableProblems.length)];
    
    setProblem(selected);
    setCode(selected.starterCode);
    setShowIntake(false);
  };

  const resetCode = () => {
    if (problem) {
      setCode(problem.starterCode);
    }
  };

  const changeProblem = (newProblem: CodingProblem) => {
    setProblem(newProblem);
    setCode(newProblem.starterCode);
    setShowProblemSelector(false);
  };

  const handleSendForReview = () => {
    if (problem) {
      onSendCode(code, problem, userContext);
    }
  };

  // Intake form
  if (showIntake) {
    return (
      <div className={cn("flex flex-col h-full bg-background", className)}>
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-2">
            <Code className="w-4 h-4 text-primary" />
            <span className="font-semibold text-sm">Coding Practice Setup</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Intake Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold mb-2">Let's calibrate your practice</h2>
            <p className="text-sm text-muted-foreground">
              This helps me select appropriate problems and tailor my feedback to your goals.
            </p>
          </div>

          {/* Experience Level */}
          <div>
            <label className="text-sm font-medium mb-2 block">Your Experience Level</label>
            <div className="grid grid-cols-2 gap-2">
              {experienceLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setUserContext(prev => ({ ...prev, experienceLevel: level.id }))}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left",
                    userContext.experienceLevel === level.id
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <level.icon className={cn(
                    "w-5 h-5",
                    userContext.experienceLevel === level.id ? "text-primary" : "text-muted-foreground"
                  )} />
                  <div>
                    <div className="font-medium text-sm">{level.label}</div>
                    <div className="text-xs text-muted-foreground">{level.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Target Company */}
          <div>
            <label className="text-sm font-medium mb-2 block">Target Company Type</label>
            <div className="grid grid-cols-2 gap-2">
              {companyTypes.map((company) => (
                <button
                  key={company.id}
                  onClick={() => setUserContext(prev => ({ ...prev, targetCompany: company.id }))}
                  className={cn(
                    "p-3 rounded-lg border-2 transition-all text-left",
                    userContext.targetCompany === company.id
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="font-medium text-sm">{company.label}</div>
                  <div className="text-xs text-muted-foreground">{company.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Python Background */}
          <div>
            <label className="text-sm font-medium mb-2 block">Python Background</label>
            <div className="grid grid-cols-2 gap-2">
              {pythonBackgrounds.map((bg) => (
                <button
                  key={bg.id}
                  onClick={() => setUserContext(prev => ({ ...prev, pythonBackground: bg.id }))}
                  className={cn(
                    "p-3 rounded-lg border-2 transition-all text-left",
                    userContext.pythonBackground === bg.id
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="font-medium text-sm">{bg.label}</div>
                  <div className="text-xs text-muted-foreground">{bg.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Start Button */}
        <div className="p-4 border-t border-border bg-card shrink-0">
          <Button onClick={selectProblemForUser} className="w-full gap-2">
            Start Coding Practice
            <ArrowRight className="w-4 h-4" />
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            I'll select a problem matching your level and provide tailored feedback
          </p>
        </div>
      </div>
    );
  }

  // Coding interface (after intake)
  if (!problem) return null;

  return (
    <div className={cn("flex flex-col h-full bg-background", className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">Coding Practice</span>
          <Badge variant="outline" className={cn(
            "text-xs",
            problem.difficulty === 'Easy' && 'border-green-500 text-green-500',
            problem.difficulty === 'Medium' && 'border-yellow-500 text-yellow-500',
            problem.difficulty === 'Hard' && 'border-red-500 text-red-500'
          )}>
            {problem.difficulty}
          </Badge>
          <Badge variant="secondary" className="text-xs capitalize">
            {userContext.experienceLevel} → {userContext.targetCompany.replace('-', ' ')}
          </Badge>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Problem description - collapsible */}
      <div className="border-b border-border bg-secondary/30">
        <button
          onClick={() => setIsProblemExpanded(!isProblemExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-secondary/50 transition-colors"
        >
          <h3 className="font-semibold text-sm">{problem.title}</h3>
          {isProblemExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        {isProblemExpanded && (
          <div className="px-3 pb-3">
            <p className="text-sm text-muted-foreground mb-3">{problem.description}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowProblemSelector(!showProblemSelector)}
              className="text-xs"
            >
              Change Problem
            </Button>
            
            {showProblemSelector && (
              <div className="mt-2 space-y-1">
                {CODING_PROBLEMS.filter(p => p.id !== problem.id).map(p => (
                  <button
                    key={p.id}
                    onClick={() => changeProblem(p)}
                    className="w-full flex items-center justify-between p-2 text-left rounded-md hover:bg-secondary transition-colors"
                  >
                    <span className="text-sm">{p.title}</span>
                    <Badge variant="outline" className={cn(
                      "text-xs",
                      p.difficulty === 'Easy' && 'border-green-500 text-green-500',
                      p.difficulty === 'Medium' && 'border-yellow-500 text-yellow-500',
                      p.difficulty === 'Hard' && 'border-red-500 text-red-500'
                    )}>
                      {p.difficulty}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Monaco Editor */}
      <div className="flex-1 min-h-0">
        <Editor
          height="100%"
          defaultLanguage="python"
          value={code}
          onChange={(value) => setCode(value || '')}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 4,
            wordWrap: 'on',
            padding: { top: 12 },
          }}
        />
      </div>

      {/* Footer with actions */}
      <div className="flex items-center justify-between gap-2 p-3 border-t border-border bg-card shrink-0">
        <Button variant="ghost" size="sm" onClick={resetCode}>
          <RotateCcw className="w-4 h-4 mr-1" />
          Reset
        </Button>
        <Button onClick={handleSendForReview} size="sm" className="gap-1">
          <Send className="w-4 h-4" />
          Send for Review
        </Button>
      </div>
    </div>
  );
};
